/**
 * Stage annotation (backgrounds and live plates), ARCHITECTURE.md §4.3.1.
 *
 * Every coordinate is a percent of the plate. `frame` records the plate size
 * the document was authored against so aspect mismatches are detectable.
 * `affordances` and `lints` are DERIVED on save by the server and never
 * hand-edited; they are still part of the wire shape.
 */
import { z } from 'zod';
import { envelope, Facing, Id, Pct, Poly, Provenance, Pt, Lint } from './common';

// ---------------------------------------------------------------------------
// Depth model
// ---------------------------------------------------------------------------

const DepthClamp = z.strictObject({ min: z.number().positive(), max: z.number().positive() });

/** Default scale clamp applied when a document omits it (§4.3.1). */
export const DEPTH_CLAMP_DEFAULT = { min: 0.2, max: 1.6 } as const;

const clamp = DepthClamp.default({ ...DEPTH_CLAMP_DEFAULT });

/**
 * Discriminated on `mode`. In `horizon` mode `horizon_y` and `reference` are
 * expected and in `bands` mode at least two bands are expected, but both are
 * enforced by the `E_DEPTH_INCOMPLETE` lint rather than the schema: lints
 * never block saving (§4.3.4), so a half-edited depth still round-trips.
 */
export const Depth = z.discriminatedUnion('mode', [
  z.strictObject({
    mode: z.literal('horizon'),
    horizon_y: Pct.optional(),
    /** A `cm`-tall thing standing at `at_y` renders `height_pct` tall. */
    reference: z.strictObject({ cm: z.number().positive(), at_y: Pct, height_pct: Pct }).optional(),
    clamp,
  }),
  z.strictObject({
    mode: z.literal('bands'),
    /** Sorted by y; scale at each band. */
    bands: z.array(z.strictObject({ y: Pct, scale: z.number().positive() })).optional(),
    clamp,
  }),
  z.strictObject({
    mode: z.literal('flat'),
    clamp,
  }),
]);
export type Depth = z.infer<typeof Depth>;

// ---------------------------------------------------------------------------
// Features
// ---------------------------------------------------------------------------

/** Sky/fly zones: scale as if standing at `y`. */
export const ScaleRef = z.discriminatedUnion('mode', [
  z.strictObject({ mode: z.literal('own_y') }),
  z.strictObject({ mode: z.literal('fixed_y'), y: Pct }),
]);
export type ScaleRef = z.infer<typeof ScaleRef>;

/**
 * Per-kind field values (H4): `KindDef.fields` of the feature's kind are stored here, keyed by
 * the field `key`. Scalars only; a value the kind's field rejects is the `W_FIELD_VALUE` lint,
 * never a failed save. Keys a kind no longer declares are kept and ignored.
 */
export const FeatureProps = z.record(z.string(), z.union([z.string(), z.number(), z.boolean()]));
export type FeatureProps = z.infer<typeof FeatureProps>;

export const Region = z.strictObject({
  /** 'r_sky', 'r_ground', nanoid ok; unique in the document. */
  id: Id,
  /** vocab(region_kind).key; unknown kinds are valid but inert (W_UNKNOWN_KIND). */
  kind: Id,
  label: z.string().optional(),
  poly: Poly,
  tags: z.array(z.string()).optional(),
  // Overrides of the kind's semantics (absent = inherit from the vocabulary):
  walkable: z.boolean().optional(),
  blocks: z.boolean().optional(),
  occludes: z.boolean().optional(),
  /** Required when occludes (AGS rule: feet_y < baseline_y => behind). Enforced by lint. */
  baseline_y: Pct.optional(),
  /** Platforms. */
  elevation: Pct.optional(),
  /** vocab(surface).key objects may rest on inside this region. */
  surface: z.string().optional(),
  scale_ref: ScaleRef.optional(),
  props: FeatureProps.optional(),
  provenance: Provenance,
});
export type Region = z.infer<typeof Region>;

const AnchorSlot = z.strictObject({ at: Pt, stand: Pt.optional(), facing: Facing.optional() });

export const Anchor = z.strictObject({
  id: Id,
  /** vocab(anchor_kind).key: door | bed | chair | table | tree | spawn | ... */
  kind: Id,
  label: z.string().optional(),
  /** Where the thing is / where an object rests. */
  at: Pt,
  /** Where a character stands to use it (default at + kind.stand_offset). */
  stand: Pt.optional(),
  facing: Facing.optional(),
  /** Optional hotspot / hide area. */
  footprint: Poly.optional(),
  /** Linked region (walk-behind tree, table top). */
  region_id: z.string().optional(),
  surface: z.string().optional(),
  capacity: z.int().min(1).optional(),
  /** Explicit seats/beds when capacity > 1. */
  slots: z.array(AnchorSlot).optional(),
  accepts: z.array(z.string()).optional(),
  affords: z.array(z.string()).optional(),
  tags: z.array(z.string()).optional(),
  props: FeatureProps.optional(),
  provenance: Provenance,
});
export type Anchor = z.infer<typeof Anchor>;

export const PortalEdge = z.enum(['left', 'right', 'top', 'bottom']);
export type PortalEdge = z.infer<typeof PortalEdge>;

export const Transition = z.enum(['cut', 'walk_off', 'fade', 'door_open']);
export type Transition = z.infer<typeof Transition>;

const PortalShape = z.strictObject({
  id: Id,
  /** vocab(portal_kind).key: door | path | stairs | water_edge | edge | magic */
  kind: Id,
  anchor_id: z.string().optional(),
  edge: PortalEdge.optional(),
  /** Absent = "leads somewhere", the story decides. */
  to: z.strictObject({ stage_uid: Id, portal_id: z.string().optional() }).optional(),
  /** Default true. */
  bidirectional: z.boolean().optional(),
  enter_facing: Facing.optional(),
  transition: Transition.optional(),
  tags: z.array(z.string()).optional(),
  props: FeatureProps.optional(),
  provenance: Provenance,
});

/** One of `anchor_id` / `edge` is required (§4.3.1). The JSON Schema cannot express this; the backend mirror must re-check it. */
export const Portal = PortalShape.refine((p) => p.anchor_id !== undefined || p.edge !== undefined, {
  message: 'A portal needs an anchor_id or an edge.',
  path: ['anchor_id'],
});
export type Portal = z.infer<typeof Portal>;

/** vocab(path_kind): walk | fly | swim | climb. */
export const StagePath = z.strictObject({
  id: Id,
  kind: Id,
  points: z.array(Pt).min(2),
  closed: z.boolean().optional(),
  label: z.string().optional(),
  props: FeatureProps.optional(),
  provenance: Provenance,
});
export type StagePath = z.infer<typeof StagePath>;

/** Live-plate cut PNG, or "the plate clipped by this region". */
export const LayerSource = z.union([
  z.strictObject({ key: Id }),
  z.strictObject({ region_id: Id }),
]);
export type LayerSource = z.infer<typeof LayerSource>;

/** Occluders and animated cut-outs. Front layers occlude characters. */
export const Layer = z.strictObject({
  id: Id,
  source: LayerSource,
  z: z.enum(['back', 'mid', 'front']),
  /** z='front' with the baseline rule (absent = always in front). */
  baseline_y: Pct.optional(),
  /** 0..2, default 1. */
  parallax: z.number().min(0).max(2).optional(),
  provenance: Provenance,
});
export type Layer = z.infer<typeof Layer>;

// ---------------------------------------------------------------------------
// Body and envelope
// ---------------------------------------------------------------------------

export const StageBody = z.strictObject({
  /** Plate size the document was authored against. */
  frame: z.strictObject({ w: z.int().positive(), h: z.int().positive() }),
  title: z.string().optional(),
  scene_type: z.string().optional(),
  world: z.string().optional(),
  place: z.string().optional(),
  time_of_day: z.string().optional(),
  depth: Depth,
  regions: z.array(Region),
  anchors: z.array(Anchor),
  portals: z.array(Portal),
  paths: z.array(StagePath),
  layers: z.array(Layer),
  /** Asset uids / vocab keys. */
  audio: z.strictObject({ ambient: z.string().optional(), bgm_mood: z.string().optional() }).optional(),
  /** vocab(tag) keys. */
  tags: z.array(z.string()),
  /** DERIVED on save (§4.3.5); never hand-edited. */
  affordances: z.array(z.string()),
  /** Feature ids a human deleted; re-runs may not resurrect them. */
  tombstones: z.array(z.string()),
  /** DERIVED on save. */
  lints: z.array(Lint),
});
export type StageBody = z.infer<typeof StageBody>;

export const StageAnnotation = envelope('stage', StageBody).meta({
  id: 'StageAnnotation',
  title: 'Stage annotation',
  description: 'Annotation document for a background or live plate (story-studio.stage/1).',
});
export type StageAnnotation = z.infer<typeof StageAnnotation>;
